import { useEffect, useState } from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { type SequenceTemplate } from '@/models/sequenceTemplate'
import { NewSequenceTemplateButton } from './NewSequenceTemplateButton'
import { SequenceTemplateEditor } from './SequenceTemplateEditor'
import { SequenceTemplateListItem } from './SequenceTemplateListItem'

interface SequenceTemplatesPaneProps {
  templates: SequenceTemplate[]
  isLoading: boolean
  /** Which kind New template creates. */
  isGlobal: boolean
  emptyText: string
}

export function SequenceTemplatesPane({
  templates,
  isLoading,
  isGlobal,
  emptyText,
}: SequenceTemplatesPaneProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Nothing picked yet, or the pick was deleted: open the first.
  const selected = templates.find((t) => t.id === selectedId) ?? templates.at(0) ?? null

  // Pin what opened by default, so a re-sort after saving a rename doesn't switch templates.
  useEffect(() => {
    if (selected && selected.id !== selectedId) setSelectedId(selected.id)
  }, [selected, selectedId])

  return (
    <Card>
      <CardContent className="grid min-h-[600px] grid-cols-1 items-start gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <NewSequenceTemplateButton isGlobal={isGlobal} onCreated={setSelectedId} />
          {isLoading ? (
            <Skeleton className="h-14 w-full" />
          ) : templates.length === 0 ? (
            <p className="text-muted-foreground text-sm">{emptyText}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {templates.map((t) => (
                <li key={t.id}>
                  <SequenceTemplateListItem
                    template={t}
                    selected={t.id === selected?.id}
                    onSelect={() => setSelectedId(t.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
        <SequenceTemplateEditor key={selected?.id ?? 'empty'} template={selected} />
      </CardContent>
    </Card>
  )
}
